/**
 * Хук для работы с услугами клиники: список, создание, обновление, привязка шаблонов
 * Дата: 29.01.2026 - Создан для TODO-038 (Services Module)
 * Дата: 30.01.2026 - Добавлена поддержка tenant_id для SuperAdmin (queryKey)
 * Связан с: frontend/src/api/medicalServices.ts, frontend/src/pages/Services.tsx
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { medicalServicesApi, MedicalService, ServiceFilters } from '@/api/medicalServices';
import { useAuthStore } from '@/store/authStore';

export function useMedicalServices(filters?: ServiceFilters, offset = 0, limit = 20) {
  const queryClient = useQueryClient();
  const { getEffectiveTenantId } = useAuthStore();
  const tenantId = getEffectiveTenantId();

  const servicesQuery = useQuery({
    queryKey: ['medical-services', tenantId, filters, offset, limit],
    queryFn: () => medicalServicesApi.list(filters, offset, limit),
    enabled: !!tenantId, // Не запрашивать без tenant_id
  });

  const categoriesQuery = useQuery({
    queryKey: ['service-categories', tenantId],
    queryFn: () => medicalServicesApi.listCategories(),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<MedicalService>) => medicalServicesApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['medical-services'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MedicalService> }) =>
      medicalServicesApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['medical-services'] }),
  });

  const linkTemplateMutation = useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: any }) =>
      medicalServicesApi.linkTemplate(serviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical-services'] });
      queryClient.invalidateQueries({ queryKey: ['service-templates'] });
    },
  });

  const unlinkTemplateMutation = useMutation({
    mutationFn: ({ serviceId, templateId, delayHours }: { serviceId: string; templateId: string; delayHours?: number }) =>
      medicalServicesApi.unlinkTemplate(serviceId, templateId, delayHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medical-services'] });
      queryClient.invalidateQueries({ queryKey: ['service-templates'] });
    },
  });

  return {
    services: servicesQuery.data?.services || [],
    total: servicesQuery.data?.total || 0,
    categories: categoriesQuery.data?.categories || [],
    isLoading: servicesQuery.isLoading || categoriesQuery.isLoading,
    createService: createMutation.mutateAsync,
    updateService: updateMutation.mutateAsync,
    linkTemplate: linkTemplateMutation.mutateAsync,
    unlinkTemplate: unlinkTemplateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isLinking: linkTemplateMutation.isPending,
    isUnlinking: unlinkTemplateMutation.isPending,
  };
}

export function useServiceTemplates(serviceId: string) {
  const templatesQuery = useQuery({
    queryKey: ['service-templates', serviceId],
    queryFn: () => medicalServicesApi.getServiceTemplates(serviceId),
    enabled: !!serviceId,
  });

  return {
    templates: templatesQuery.data?.templates || [],
    isLoading: templatesQuery.isLoading,
  };
}
